import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { facilityService, FacilityDetail, TimeSlot } from 'services/facilityService';
import { reservationService, AlarmOption, CreateReservationRequest, CreateReservationResponse } from 'services/reservationService';
import dayIcon from 'assets/ReserveImage/Day.png';
import timeIcon from 'assets/ReserveImage/time.png';
import peopleIcon from 'assets/ReserveImage/people.png';
import alarmIcon from 'assets/ReserveImage/Alarm.png';
import choose1Icon from 'assets/ReserveImage/choose1.png';
import choose2Icon from 'assets/ReserveImage/choose2.png';
import {
  Body,
  Back,
  InfoContainer,
  InfoImg,
  Info,
  InfoTitle,
  InfoLocation,
  Line,
  DayTitle,
  CalContainer,
  CalendarHeader,
  CalendarWeekdays,
  CalendarDates,
  DateCell,
  ConfirmButton,
  ConfirmButtonWrapper,
  GuestTooltip,
  TimeSection,
  SectionTitle,
  TimeTitleRow,
  LegendContainer,
  LegendItem,
  TimeSlots,
  TimeSlotButton,
  PersonSection,
  PersonControl,
  PersonButton,
  PersonCount,
  AlarmSection,
  AlarmHeader,
  AlarmToggle,
  AlarmOptions,
  AlarmOptionButton,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalImageContainer,
  ModalImage,
  ModalInfoSection,
  ModalInfoTitle,
  ModalInfoLocation,
  ModalInfoRow,
  ModalInfoLabel,
  ModalInfoValue,
  ModalButton
} from './Reserve.styles';

function Reserve() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isGuest = localStorage.getItem("guestMode") === "true";
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const [facilityData, setFacilityData] = useState<FacilityDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Time selection state
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<TimeSlot[]>([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState<boolean>(false);
  const [reservationLimitMinutes, setReservationLimitMinutes] = useState<number | null>(null);

  // Person count state
  const [personCount, setPersonCount] = useState<number>(1);

  // Alarm states
  const [alarmEnabled, setAlarmEnabled] = useState<boolean>(true);
  const [alarmOptions, setAlarmOptions] = useState<AlarmOption[]>([]);
  const [selectedAlarms, setSelectedAlarms] = useState<string[]>([]);

  // Modal state
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [reservationData, setReservationData] = useState<CreateReservationResponse | null>(null);

  // Fetch facility data
  useEffect(() => {
    const fetchFacilityData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await facilityService.getFacilityDetail(Number(id));
        setFacilityData(data);
        console.log('Facility data:', data);
      } catch (error) {
        console.error('Error fetching facility data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFacilityData();
  }, [id]);

  // Fetch alarm options
  useEffect(() => {
    const fetchAlarmOptions = async () => {
      try {
        const options = await reservationService.getAlarmOptions();
        setAlarmOptions(options);
        console.log('Alarm options:', options);
      } catch (error) {
        console.error('Error fetching alarm options:', error);
      }
    };

    fetchAlarmOptions();
  }, []);

  // Fetch time slots when date is selected
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedDate || !id) {
        return;
      }

      setIsLoadingTimes(true);
      try {
        // Format date as YYYY-MM-DD in local timezone
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        console.log('Fetching times for date:', dateString);
        const response = await facilityService.getFacilityTimes(Number(id), dateString);
        setTimeSlots(response.timeSlots);
        setReservationLimitMinutes(response.reservationLimitMinutes);
        console.log('Time slots:', response.timeSlots);
        console.log('Reservation limit:', response.reservationLimitMinutes);
      } catch (error) {
        console.error('Error fetching time slots:', error);
        setTimeSlots([]);
      } finally {
        setIsLoadingTimes(false);
      }
    };

    fetchTimeSlots();
  }, [selectedDate, id]);

  const handleGoBack = () => {
    navigate(`/facilities/${id}`, { replace: true });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    // Prevent selecting past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDay = new Date(date);
    selectedDay.setHours(0, 0, 0, 0);

    if (selectedDay < today) {
      return; // Don't select past dates
    }

    setSelectedDate(date);
    setSelectedTimes([]); // Reset selected times when date changes
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const handleConfirm = async () => {
    if (!selectedDate || selectedTimes.length === 0 || personCount <= 0 || !id) {
      return;
    }

    try {
      // Sort selected times by startTime
      const sortedTimes = [...selectedTimes].sort((a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );

      // Calculate total duration
      const totalDuration = sortedTimes.reduce((sum, slot) => sum + slot.durationMinutes, 0);

      const requestData: CreateReservationRequest = {
        facilityId: Number(id),
        headCount: personCount,
        startTime: sortedTimes[0].startTime,
        durationMinutes: totalDuration,
        alarmTimings: alarmEnabled ? selectedAlarms : [],
      };

      console.log('Creating reservation:', requestData);
      const response = await reservationService.createNewReservation(requestData);

      // Save reservation data and show success modal
      setReservationData(response);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error creating reservation:', error);
      const errorMessage = error.response?.data?.message || '예약 생성에 실패했습니다. 다시 시도해주세요.';
      alert(errorMessage);
    }
  };

  const handleTimeClick = (timeSlot: TimeSlot) => {
    // Prevent selecting past times or booked times
    if (timeSlot.isBooked || isPastTime(timeSlot.startTime)) {
      return;
    }

    // Check if already selected
    const isSelected = selectedTimes.some(t => t.startTime === timeSlot.startTime);

    if (isSelected) {
      // Remove clicked slot and all slots after it
      const clickedTime = new Date(timeSlot.startTime).getTime();
      setSelectedTimes(prev =>
        prev.filter(t => new Date(t.startTime).getTime() < clickedTime)
      );
    } else {
      // Check if this slot would be consecutive
      if (selectedTimes.length > 0) {
        const sortedSelectedTimes = [...selectedTimes].sort((a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );

        const firstSelectedTime = sortedSelectedTimes[0];
        const lastSelectedTime = sortedSelectedTimes[sortedSelectedTimes.length - 1];

        const newStartTime = new Date(timeSlot.startTime).getTime();
        const newEndTime = newStartTime + timeSlot.durationMinutes * 60 * 1000;

        const firstStartTime = new Date(firstSelectedTime.startTime).getTime();
        const lastEndTime = new Date(lastSelectedTime.startTime).getTime() + lastSelectedTime.durationMinutes * 60 * 1000;

        // Check if the new slot is consecutive (either before first or after last)
        const isConsecutiveAfter = newStartTime === lastEndTime;
        const isConsecutiveBefore = newEndTime === firstStartTime;

        if (!isConsecutiveAfter && !isConsecutiveBefore) {
          return; // Not consecutive, don't select
        }
      }

      // Check if adding this slot would exceed the reservation limit
      if (reservationLimitMinutes !== null) {
        const currentTotal = selectedTimes.reduce((sum, slot) => sum + slot.durationMinutes, 0);
        const newTotal = currentTotal + timeSlot.durationMinutes;

        if (newTotal > reservationLimitMinutes) {
          return; // Exceeds limit, silently don't select
        }
      }

      // Add to selection
      setSelectedTimes(prev => [...prev, timeSlot]);
    }
  };

  const isTimeSlotSelected = (timeSlot: TimeSlot): boolean => {
    return selectedTimes.some(t => t.startTime === timeSlot.startTime);
  };

  const isPastTime = (isoString: string): boolean => {
    const now = new Date();
    const timeSlotDate = new Date(isoString);
    return timeSlotDate < now;
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  const handlePersonIncrease = () => {
    setPersonCount(prev => prev + 1);
  };

  const handlePersonDecrease = () => {
    if (personCount > 1) {
      setPersonCount(prev => prev - 1);
    }
  };

  const handleAlarmToggle = (alarmName: string) => {
    if (selectedAlarms.includes(alarmName)) {
      setSelectedAlarms(prev => prev.filter(a => a !== alarmName));
    } else {
      setSelectedAlarms(prev => [...prev, alarmName]);
    }
  };

  const isAlarmOptionDisabled = (option: AlarmOption): boolean => {
    if (selectedTimes.length === 0) return false;

    // Get the earliest selected time
    const earliestTime = selectedTimes.reduce((earliest, slot) => {
      const slotTime = new Date(slot.startTime);
      return slotTime < earliest ? slotTime : earliest;
    }, new Date(selectedTimes[0].startTime));

    const now = new Date();
    const minutesUntilSlot = (earliestTime.getTime() - now.getTime()) / (1000 * 60);

    // If the alarm time has already passed, disable it
    return minutesUntilSlot < option.minutes;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(new Date(0)); // Placeholder for empty cells
    }

    // Add actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatMonthYear = (date: Date): string => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const isSameDay = (date1: Date | null, date2: Date): boolean => {
    if (!date1) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const days = getDaysInMonth(currentDate);
  const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  if (isLoading) {
    return (
      <Body>
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
      </Body>
    );
  }

  return (
    <Body>
      <Back>
        <button onClick={handleGoBack} className="Backbutton">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="title">예약</div>
      </Back>

      <InfoContainer>
        <InfoImg>
          {facilityData?.imageUrl && (
            <img
              src={facilityData.imageUrl}
              alt={facilityData.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
            />
          )}
        </InfoImg>
        <Info>
          <InfoTitle>{facilityData?.name}</InfoTitle>
          <InfoLocation>{facilityData?.building.name} {facilityData?.location}</InfoLocation>
        </Info>
      </InfoContainer>

      <Line />

      <DayTitle>
        <img src={dayIcon} alt="날짜" className="icon" style={{ width: '20px', height: '20px' }} />
        <div className="title">이용 날짜</div>
      </DayTitle>

      <CalContainer>
        <CalendarHeader>
          <button onClick={handlePrevMonth} className="arrow">&lt;</button>
          <span className="month-year">{formatMonthYear(currentDate)}</span>
          <button onClick={handleNextMonth} className="arrow">&gt;</button>
        </CalendarHeader>

        <CalendarWeekdays>
          {weekdays.map((day) => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </CalendarWeekdays>

        <CalendarDates>
          {days.map((date, index) => (
            <DateCell
              key={index}
              isEmpty={date.getTime() === 0}
              isSelected={isSameDay(selectedDate, date)}
              isPast={date.getTime() !== 0 && isPastDate(date)}
              onClick={() => date.getTime() !== 0 && handleDateClick(date)}
            >
              {date.getTime() !== 0 ? date.getDate() : ''}
            </DateCell>
          ))}
        </CalendarDates>
      </CalContainer>

      {selectedDate && (
        <>
          <Line />

          <TimeSection>
            <TimeTitleRow>
              <SectionTitle>
                <img src={timeIcon} alt="시간" className="icon" style={{ width: '20px', height: '20px' }} />
                <div className="title">이용 시간</div>
                {reservationLimitMinutes && (
                  <div style={{
                    marginLeft: '8px',
                    fontSize: '13px',
                    color: '#666',
                    fontWeight: '400'
                  }}>
                    (최대 {reservationLimitMinutes}분)
                  </div>
                )}
              </SectionTitle>

              <LegendContainer>
                <LegendItem>
                  <img src={choose1Icon} alt="선택가능" />
                  <span>선택가능</span>
                </LegendItem>
                <LegendItem>
                  <img src={choose2Icon} alt="선택불가능" />
                  <span>선택불가능</span>
                </LegendItem>
              </LegendContainer>
            </TimeTitleRow>

            {isLoadingTimes ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                시간 조회 중...
              </div>
            ) : timeSlots.length > 0 ? (
              <TimeSlots>
                {timeSlots.map((slot, index) => {
                  const isPast = isPastTime(slot.startTime);
                  const isDisabled = slot.isBooked || isPast;
                  const isSelected = isTimeSlotSelected(slot);

                  return (
                    <TimeSlotButton
                      key={index}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      onClick={() => handleTimeClick(slot)}
                    >
                      {formatTime(slot.startTime)}
                    </TimeSlotButton>
                  );
                })}
              </TimeSlots>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                이용 가능한 시간이 없습니다.
              </div>
            )}
          </TimeSection>

          <Line />

          <PersonSection>
            <SectionTitle>
              <img src={peopleIcon} alt="인원" className="icon" style={{ width: '20px', height: '20px' }} />
              <div className="title">이용 인원</div>
            </SectionTitle>

            <PersonControl>
              <PersonButton onClick={handlePersonDecrease} disabled={personCount <= 1}>
                −
              </PersonButton>
              <PersonCount>{personCount}명</PersonCount>
              <PersonButton onClick={handlePersonIncrease}>
                +
              </PersonButton>
            </PersonControl>
          </PersonSection>

          <Line />

          <AlarmSection>
            <AlarmHeader>
              <SectionTitle>
                <img src={alarmIcon} alt="알람" className="icon" style={{ width: '20px', height: '20px' }} />
                <div className="title">알람 설정</div>
              </SectionTitle>

              <AlarmToggle>
                <input
                  type="checkbox"
                  checked={alarmEnabled}
                  onChange={(e) => setAlarmEnabled(e.target.checked)}
                />
                <span className="slider"></span>
              </AlarmToggle>
            </AlarmHeader>

            {alarmEnabled && (
              <AlarmOptions>
                {alarmOptions.map((option) => {
                  const isDisabled = isAlarmOptionDisabled(option);
                  return (
                    <AlarmOptionButton
                      key={option.name}
                      isSelected={selectedAlarms.includes(option.name)}
                      isDisabled={isDisabled}
                      onClick={() => !isDisabled && handleAlarmToggle(option.name)}
                    >
                      {option.displayName}
                    </AlarmOptionButton>
                  );
                })}
              </AlarmOptions>
            )}
          </AlarmSection>

          <Line />
        </>
      )}

      <ConfirmButtonWrapper
        onMouseEnter={() => isGuest && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => isGuest && setShowTooltip(true)}
      >
        <GuestTooltip visible={showTooltip}>로그인이 필요합니다</GuestTooltip>
        <ConfirmButton
          className={!isGuest && selectedDate && selectedTimes.length > 0 ? 'active' : ''}
          onClick={isGuest ? undefined : handleConfirm}
          isGuestMode={isGuest}
        >
          확인
        </ConfirmButton>
      </ConfirmButtonWrapper>

      {showSuccessModal && reservationData && (
        <ModalOverlay onClick={() => {
          setShowSuccessModal(false);
          navigate('/home');
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <ModalTitle style={{ color: 'white', marginBottom: '4px' }}>
                {reservationData.member.name}님의 <span style={{ color: '#1FBC70' }}>&lt;{reservationData.facility.name}&gt;</span>
              </ModalTitle>
              <ModalSubtitle>예약이 완료되었습니다.</ModalSubtitle>
            </div>

            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalCloseButton onClick={() => {
                setShowSuccessModal(false);
                navigate('/home');
              }}>
                ×
              </ModalCloseButton>

              <ModalHeader>
                <ModalImageContainer>
                  <ModalImage
                    src={reservationData.facility.imageUrl || ''}
                    alt={reservationData.facility.name}
                  />
                </ModalImageContainer>
              </ModalHeader>

              <ModalInfoSection>
                <ModalInfoTitle>{reservationData.facility.name}</ModalInfoTitle>
                <ModalInfoLocation>
                  {reservationData.facility.location}
                </ModalInfoLocation>

                <ModalInfoRow>
                  <ModalInfoLabel>이용일자 : </ModalInfoLabel>
                  <ModalInfoValue>
                    {new Date(reservationData.startTime).getFullYear()}-
                    {String(new Date(reservationData.startTime).getMonth() + 1).padStart(2, '0')}-
                    {String(new Date(reservationData.startTime).getDate()).padStart(2, '0')}
                  </ModalInfoValue>
                </ModalInfoRow>

                <ModalInfoRow>
                  <ModalInfoLabel>이용시간 : </ModalInfoLabel>
                  <ModalInfoValue>
                    {formatTime(reservationData.startTime)} - {formatTime(reservationData.endTime)}
                  </ModalInfoValue>
                </ModalInfoRow>
              </ModalInfoSection>

              <ModalButton onClick={() => {
                setShowSuccessModal(false);
                navigate('/reservations');
              }}>
                예약내역
              </ModalButton>
            </ModalContent>
          </div>
        </ModalOverlay>
      )}
    </Body>
  );
}

export default Reserve;
